const REVIEW_STATUS_CLASSES = [
  'diff-item--accepted',
  'diff-item--rejected',
  'diff-item--resolved',
  'diff-item--manual',
];

const REVIEW_STATUSES = new Set([
  'unresolved',
  'accepted',
  'rejected',
  'manual',
]);

function reviewMarkers(root, itemIndex) {
  if (!root || typeof root.querySelectorAll !== 'function') return [];
  return root.querySelectorAll(`[diff-index="${itemIndex}"]`);
}

/**
 * Set the review model status and derive every marker's resolution classes
 * from it. Callers must not update `_status` and marker classes separately.
 */
export function setReviewItemStatus(items, itemIndex, status, root = document) {
  if (!Array.isArray(items) || !items[itemIndex]) {
    throw new TypeError(`No review item exists at index ${itemIndex}`);
  }
  if (!REVIEW_STATUSES.has(status)) {
    throw new TypeError(`Unsupported review status: ${status}`);
  }

  const item = items[itemIndex];
  item._status = status;

  reviewMarkers(root, itemIndex).forEach(marker => {
    marker.classList.remove(...REVIEW_STATUS_CLASSES);

    if (status === 'accepted') {
      marker.classList.add('diff-item--accepted', 'diff-item--resolved');
    } else if (status === 'rejected') {
      marker.classList.add('diff-item--rejected', 'diff-item--resolved');
    } else if (status === 'manual') {
      marker.classList.add('diff-item--resolved');
      marker.classList.add(
        marker.classList.contains('diff-item--blocked')
          ? 'diff-item--manual'
          : 'diff-item--rejected',
      );
    }
  });

  return item;
}

/**
 * A request only counts against the drafter if the drafter has a surface to
 * resolve it on. `createAnnotations` (lease_editor_controller.js:11826-11832)
 * builds no annotation for ghost, unsupported, or static items, and the
 * markers of items folded into a previous item are removed outright
 * (:9875-9879). Counting any of those would raise an exit warning whose count
 * can never reach zero — worst of all on the unsupported-requests modal, whose
 * own exit button calls `exitReviewMode`
 * (version_control_unsupported_modal_directive.js:18).
 */
function isDrafterResolvable(item) {
  return Boolean(
    item &&
      !item._isGhostItem &&
      !item._includedInPreviousItem &&
      !item.unsupportedItem &&
      !item.IsStatic,
  );
}

export function countUnresolvedChanges(items) {
  if (!Array.isArray(items)) return 0;

  return items.filter(
    item => isDrafterResolvable(item) && item._status === 'unresolved',
  ).length;
}

export function unresolvedReviewWarning(unresolvedChangeCount) {
  const noun = unresolvedChangeCount === 1 ? 'change' : 'changes';
  return (
    `You still have ${unresolvedChangeCount} unresolved ${noun}. ` +
    'If you finish the review now, those proposed changes will not be applied. Continue?'
  );
}

export function confirmExitWithUnresolvedChanges(
  unresolvedChangeCount,
  confirmExit,
) {
  if (unresolvedChangeCount === 0) return true;
  if (typeof confirmExit !== 'function') {
    throw new TypeError('confirmExit must be a function');
  }
  return confirmExit(unresolvedReviewWarning(unresolvedChangeCount));
}
